import React, { useState, useEffect, useCallback, useRef } from 'react';
import { googleAuthAPI } from '../../services/api';

const ConnectGmail = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [labelDraft, setLabelDraft] = useState('');
  const labelInputRef = useRef(null);

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await googleAuthAPI.getConnectedAccounts();
      setAccounts(res.data);
    } catch (err) {
      console.error('Error loading connected accounts:', err);
      setError('Failed to load connected accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Focus the label input when editing starts.
  useEffect(() => {
    if (editingLabelId !== null && labelInputRef.current) {
      labelInputRef.current.focus();
      labelInputRef.current.select();
    }
  }, [editingLabelId]);

  const handleConnect = async () => {
    try {
      const res = await googleAuthAPI.getAuthUrl();
      window.location.href = res.data.url;
    } catch (err) {
      console.error('Error getting auth URL:', err);
      setError('Failed to start Google sign-in.');
    }
  };

  const handleDisconnect = async (account) => {
    const display = account.label || account.accountEmail || `Account #${account.id}`;
    if (!window.confirm(`Disconnect "${display}" from your Gmail integration?`)) return;
    setBusyId(account.id);
    setError('');
    try {
      await googleAuthAPI.disconnectAccount(account.id);
      await loadAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to disconnect.');
    } finally {
      setBusyId(null);
    }
  };

  const handleSetPrimary = async (account) => {
    setBusyId(account.id);
    setError('');
    try {
      await googleAuthAPI.updateAccount(account.id, { isPrimary: true });
      await loadAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set primary.');
    } finally {
      setBusyId(null);
    }
  };

  const startEditLabel = (account) => {
    setEditingLabelId(account.id);
    setLabelDraft(account.label || '');
  };

  const cancelEditLabel = () => {
    setEditingLabelId(null);
    setLabelDraft('');
  };

  const saveLabel = async (account) => {
    setBusyId(account.id);
    setError('');
    try {
      await googleAuthAPI.updateAccount(account.id, { label: labelDraft });
      setEditingLabelId(null);
      setLabelDraft('');
      await loadAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update label.');
    } finally {
      setBusyId(null);
    }
  };

  const handleLabelKey = (e, account) => {
    if (e.key === 'Enter') saveLabel(account);
    else if (e.key === 'Escape') cancelEditLabel();
  };

  return (
    <div className="gmail-connect-card">
      <div className="gmail-connect-header">
        <i className="fas fa-envelope gmail-icon"></i>
        <h3>Gmail Accounts</h3>
      </div>
      <p className="gmail-connect-desc">
        Connect one or more Gmail accounts. Pick which account to send from when composing emails.
      </p>

      {error && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{error}</div>}

      {loading ? (
        <div className="gmail-status">Loading…</div>
      ) : accounts.length === 0 ? (
        <p className="gmail-account-empty">
          No Gmail accounts connected yet.
        </p>
      ) : (
        <ul className="gmail-account-list">
          {accounts.map(acc => {
            const busy = busyId === acc.id;
            const isEditing = editingLabelId === acc.id;
            return (
              <li key={acc.id} className="gmail-account-row">
                <div className="gmail-account-info">
                  <div className="gmail-account-email">
                    <span>{acc.accountEmail}</span>
                    {acc.isPrimary && <span className="gmail-account-primary-pill">Primary</span>}
                  </div>

                  {isEditing ? (
                    <div className="gmail-account-label-edit">
                      <input
                        ref={labelInputRef}
                        className="gmail-account-label-input"
                        type="text"
                        value={labelDraft}
                        onChange={(e) => setLabelDraft(e.target.value)}
                        onKeyDown={(e) => handleLabelKey(e, acc)}
                        placeholder="e.g. Personal, Repairs"
                      />
                      <button
                        type="button"
                        className="gmail-account-btn primary-action"
                        onClick={() => saveLabel(acc)}
                        disabled={busy}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="gmail-account-btn"
                        onClick={cancelEditLabel}
                        disabled={busy}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="gmail-account-label-line">
                      <span>{acc.label ? `Label: ${acc.label}` : 'No label'}</span>
                      <button
                        type="button"
                        className="gmail-account-label-edit-btn"
                        onClick={() => startEditLabel(acc)}
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>

                {!isEditing && (
                  <div className="gmail-account-actions">
                    {!acc.isPrimary && (
                      <button
                        type="button"
                        className="gmail-account-btn"
                        onClick={() => handleSetPrimary(acc)}
                        disabled={busy}
                        title="Set as primary sender"
                      >
                        Set primary
                      </button>
                    )}
                    <button
                      type="button"
                      className="gmail-account-btn danger"
                      onClick={() => handleDisconnect(acc)}
                      disabled={busy}
                      title="Disconnect this account"
                    >
                      Disconnect
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <button className="btn-connect-gmail" onClick={handleConnect}>
        <i className="fab fa-google"></i>
        {accounts.length === 0 ? 'Connect Gmail' : 'Connect Another Account'}
      </button>
    </div>
  );
};

export default ConnectGmail;
